const STORAGE_KEY = 'admin-ui-preferences'
const FONT_SIZE_BASE = 14
const PREFERENCE_VERSION = 7

export const preferenceDefaults = {
  theme: 'light',
  accent: 'violet',
  customAccent: '#2f6fed',
  customThemeColor: '#172033',
  fontSize: FONT_SIZE_BASE,
  preferenceVersion: PREFERENCE_VERSION,
  compact: false,
  glass: true,
  motion: true,
  sidebarCollapsed: false,
  hiddenMenus: []
}

function readStorage() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {}
    if (Number(stored.preferenceVersion || 0) < PREFERENCE_VERSION) {
      if (stored.fontSize === undefined || Number(stored.fontSize) < FONT_SIZE_BASE) stored.fontSize = preferenceDefaults.fontSize
      if (Number(stored.preferenceVersion || 0) < 7) stored.hiddenMenus = []
      stored.preferenceVersion = PREFERENCE_VERSION
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
    }
    return stored
  } catch (error) {
    return {}
  }
}

export function getPreferences() {
  return { ...preferenceDefaults, ...readStorage() }
}

export function savePreferences(patch) {
  const preferences = { ...getPreferences(), ...(patch || {}) }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences))
  return preferences
}

function hexToRgb(color) {
  const raw = String(color || '').replace('#', '').trim()
  const hex = raw.length === 3 ? raw.split('').map(item => item + item).join('') : raw
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null
  const value = parseInt(hex, 16)
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 }
}

function rgbToHex(rgb) {
  return `#${[rgb.r, rgb.g, rgb.b].map(value => value.toString(16).padStart(2, '0')).join('')}`
}

export function normalizeColor(color, fallback = preferenceDefaults.customAccent) {
  const rgb = hexToRgb(color)
  return rgb ? rgbToHex(rgb) : fallback
}

function mix(color, target, ratio) {
  const source = hexToRgb(color)
  const destination = hexToRgb(target)
  if (!source || !destination) return color
  const channel = key => Math.round(source[key] * (1 - ratio) + destination[key] * ratio)
  return rgbToHex({ r: channel('r'), g: channel('g'), b: channel('b') })
}

function luminance(color) {
  const rgb = hexToRgb(color)
  return rgb ? (rgb.r * 0.299 + rgb.g * 0.587 + rgb.b * 0.114) / 255 : 0
}

function clearInlineVariables(root) {
  [
    '--accent-rgb', '--primary', '--primary-dark', '--primary-soft', '--sidebar-active',
    '--bg', '--surface', '--surface-raised', '--sidebar', '--header', '--text',
    '--muted', '--border', '--control', '--table-header', '--table-row', '--table-row-alt',
    '--glass-surface', '--glass-raised', '--theme-rgb', '--app-font-size-base', '--font-size-adjustment'
  ].forEach(name => root.style.removeProperty(name))
}

function applyCustomTheme(root, color) {
  const themeColor = normalizeColor(color, preferenceDefaults.customThemeColor)
  const rgb = hexToRgb(themeColor)
  const dark = luminance(themeColor) < 0.58
  root.setAttribute('data-theme', dark ? 'dark' : 'light')
  root.style.setProperty('--bg', dark ? mix(themeColor, '#000000', 0.72) : mix(themeColor, '#ffffff', 0.88))
  root.style.setProperty('--surface', dark ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, .72)` : mix(themeColor, '#ffffff', 0.96))
  root.style.setProperty('--surface-raised', dark ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, .82)` : '#ffffff')
  root.style.setProperty('--sidebar', dark ? mix(themeColor, '#000000', 0.42) : mix(themeColor, '#000000', 0.36))
  root.style.setProperty('--header', dark ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, .74)` : '#ffffff')
  root.style.setProperty('--glass-surface', dark ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, .58)` : 'rgba(255, 255, 255, .68)')
  root.style.setProperty('--glass-raised', dark ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, .86)` : 'rgba(255, 255, 255, .82)')
  root.style.setProperty('--text', dark ? '#e7ecf4' : '#172033')
  root.style.setProperty('--muted', dark ? '#98a6ba' : '#6b778c')
  root.style.setProperty('--border', dark ? 'rgba(148, 163, 184, 0.20)' : '#e3e8ef')
  root.style.setProperty('--control', dark ? 'rgba(15, 23, 42, 0.62)' : '#ffffff')
  root.style.setProperty('--table-header', dark ? 'rgba(30, 41, 59, 0.86)' : '#f7f9fc')
  root.style.setProperty('--table-row', dark ? 'rgba(15, 23, 42, .42)' : 'rgba(255, 255, 255, .52)')
  root.style.setProperty('--table-row-alt', dark ? 'rgba(15, 23, 42, .28)' : 'rgba(248, 250, 252, .42)')
  root.style.setProperty('--theme-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`)
}

function applyCustomAccent(root, color) {
  const accent = normalizeColor(color)
  const rgb = hexToRgb(accent)
  root.style.setProperty('--accent-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`)
  root.style.setProperty('--primary', accent)
  root.style.setProperty('--primary-dark', mix(accent, '#000000', 0.22))
  root.style.setProperty('--primary-soft', mix(accent, '#ffffff', 0.88))
  root.style.setProperty('--sidebar-active', accent)
}

export function applyPreferences(preferences) {
  const next = { ...preferenceDefaults, ...(preferences || {}) }
  const root = document.documentElement
  root.classList.add('theme-switching')
  clearInlineVariables(root)
  if (next.theme === 'custom') applyCustomTheme(root, next.customThemeColor)
  else root.setAttribute('data-theme', next.theme === 'light' ? 'light' : 'dark')

  root.setAttribute('data-theme-mode', next.theme)
  root.setAttribute('data-accent', next.accent)
  root.setAttribute('data-density', next.compact ? 'compact' : 'comfortable')
  root.setAttribute('data-glass', next.glass ? 'on' : 'off')
  root.setAttribute('data-motion', next.motion ? 'on' : 'off')

  const fontSize = Math.min(24, Math.max(FONT_SIZE_BASE, Number(next.fontSize) || preferenceDefaults.fontSize))
  root.setAttribute('data-font-size', String(fontSize))
  root.style.setProperty('--app-font-size-base', `${fontSize}px`)
  root.style.setProperty('--font-size-adjustment', `${fontSize - FONT_SIZE_BASE}px`)

  if (next.accent === 'custom') applyCustomAccent(root, next.customAccent)

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      root.classList.remove('theme-switching')
      window.dispatchEvent(new Event('admin-theme-change'))
    })
  })
}
